import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { ManagedCustomDashboardTabsConfig } from '@/lib/api/custom-dashboard';
import { isTerminalDataUpdateStatus } from '../dataUpdateJobStatus';

/**
 * 終端（succeeded/failed）に達したジョブを jobId 単位で「初めて観測したとき」に
 * 履歴（revisions）と表示（tabs）のクエリを invalidate する。
 * 前回の描画との「遷移」ではなく jobId の初回観測で判定するため、設定画面を開いた
 * 時点で既に終端だったジョブ（例: autoActivate 完了後に画面を開いた場合）も取りこぼさない
 * （sazae#309 Bugbot 指摘）。観測済み jobId は ref の Set に保持し二重 invalidate を防ぐ。
 */
export function useDataUpdateTerminalInvalidation(
  accountId: string,
  data: ManagedCustomDashboardTabsConfig | undefined,
): void {
  const queryClient = useQueryClient();
  const observedTerminalJobIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!data) return;
    const observed = observedTerminalJobIdsRef.current;
    for (const tab of data.tabs) {
      const update = tab.latestDataUpdate;
      if (update === null || !isTerminalDataUpdateStatus(update.status)) continue;
      if (observed.has(update.jobId)) continue;
      observed.add(update.jobId);
      void queryClient.invalidateQueries({
        queryKey: ['custom-dashboard-revisions', accountId, tab.tabId],
      });
      void queryClient.invalidateQueries({ queryKey: ['custom-dashboard-tabs', accountId] });
    }
  }, [accountId, data, queryClient]);
}
